import { ChecklistSectionCreateModel } from 'shared/domain/checklistSection/types/model';
import { ChecklistItemInDto } from 'shared/dtos/in/template';
import { GeneralNormConfig } from './generalNormConfig';
import {
  ChecklistItemOutDto,
  CreateChecklistItemsOutDto,
  InspectionTemplateOutDto,
} from 'shared/dtos/out/template';
import { sortByFractionalOrder } from 'helpers/sort';

interface TemplateGeneralNormComparable {
  description: string;
}

interface TemplateComparable {
  process: string | null;
  summary: string;
  generalNorm: TemplateGeneralNormComparable;
  generalNormConfig?: GeneralNormConfig;
}

export interface TemplateChecklistItemDraft {
  _id?: string;
  uniqueKeyId: string;
  summary: string;
  description: string;
  parentSectionId: string | null;
  order: string;
}

export interface TemplateSectionDraft {
  _id?: string;
  uniqueKeyId: string;
  title: string;
  description: string;
  parentSectionId: string | null;
  order: string;
}

export interface CreatedChecklistSection {
  _id?: string;
}

export interface ChecklistChangeObject<TChecklistItem> {
  created: TChecklistItem[];
  changed: TChecklistItem[];
  deleted: TChecklistItem[];
}

type CreateChecklistSectionFn = (
  model: ChecklistSectionCreateModel
) => Promise<CreatedChecklistSection>;

export function hasTemplateMetadataChanges(
  oldValues: TemplateComparable,
  newValues: Pick<
    InspectionTemplateOutDto,
    'process' | 'summary' | 'generalNorm' | 'generalNormConfig'
  >
): boolean {
  if (oldValues.process !== newValues.process) {
    return true;
  }
  if (oldValues.summary !== newValues.summary) {
    return true;
  }

  if (oldValues.generalNorm.description !== newValues.generalNorm.description) {
    return true;
  }

  if (oldValues.generalNormConfig && newValues.generalNormConfig) {
    return (
      oldValues.generalNormConfig.isControlledPartyVisible !== newValues.generalNormConfig.isControlledPartyVisible ||
      oldValues.generalNormConfig.isContractNumberVisible !== newValues.generalNormConfig.isContractNumberVisible ||
      oldValues.generalNormConfig.isWorkTypeVisible !== newValues.generalNormConfig.isWorkTypeVisible ||
      oldValues.generalNormConfig.isCommentVisible !== newValues.generalNormConfig.isCommentVisible ||
      oldValues.generalNormConfig.isInspectionResultVisible !== newValues.generalNormConfig.isInspectionResultVisible
    );
  }

  return Boolean(oldValues.generalNormConfig) !== Boolean(newValues.generalNormConfig);
}

export function createChecklistChangeObject<TChecklistItem extends TemplateChecklistItemDraft>(
  oldValues: TChecklistItem[],
  newValues: TChecklistItem[]
): ChecklistChangeObject<TChecklistItem> {
  const startingValue: ChecklistChangeObject<TChecklistItem> = {
    created: [],
    changed: [],
    deleted: oldValues.filter(
      (oldValue) =>
        !newValues.find((newValue) => newValue._id === oldValue._id)
    ),
  };

  return newValues.reduce<ChecklistChangeObject<TChecklistItem>>((result, value) => {
    if (!value._id) {
      result.created.push(value);
      return result;
    }

    const previousValueChecklistItem = oldValues.find(
      (oldValue) => oldValue._id === value._id
    );

    if (!previousValueChecklistItem) {
      return result;
    }

    const change = optimizeChecklistChange(previousValueChecklistItem, value);
    if (change) {
      result.changed.push(change);
    }

    return result;
  }, startingValue);
}

function optimizeChecklistChange<TChecklistItem extends TemplateChecklistItemDraft>(
  oldValue: TChecklistItem,
  newValue: TChecklistItem
): TChecklistItem | undefined {
  if (oldValue.summary !== newValue.summary) {
    return newValue;
  }
  if (oldValue.description !== newValue.description) {
    return newValue;
  }
  if (oldValue.parentSectionId !== newValue.parentSectionId) {
    return newValue;
  }
  if (oldValue.order !== newValue.order) {
    return newValue;
  }

  return undefined;
}

export function toCreateChecklistItemsDto<
  TChecklistItem extends Pick<TemplateChecklistItemDraft, '_id' | 'summary' | 'description' | 'parentSectionId' | 'order'>,
>(checklist: TChecklistItem[]): CreateChecklistItemsOutDto {
  const sortedChecklist = [...checklist].sort(sortByFractionalOrder);
  return sortedChecklist.map((itemToCreate, index) => ({
    _id: itemToCreate._id,
    summary: itemToCreate.summary,
    parentSectionId: itemToCreate.parentSectionId,
    index,
    norm: {
      description: itemToCreate.description,
    },
  }));
}

export function toChecklistItemDto<
  TChecklistItem extends Pick<TemplateChecklistItemDraft, '_id' | 'summary' | 'description' | 'parentSectionId'>,
>(checklist: TChecklistItem, index?: number): ChecklistItemOutDto {
  return {
    _id: checklist._id,
    index,
    summary: checklist.summary,
    parentSectionId: checklist.parentSectionId,
    norm: {
      description: checklist.description,
    },
  };
}

export async function createSectionsWithHierarchy<
  TSection extends TemplateSectionDraft,
>(
  templateId: string,
  projectId: string,
  sections: TSection[],
  createChecklistSection: CreateChecklistSectionFn
): Promise<Map<string, string>> {
  const createdSectionIdentifierByKey = new Map<string, string>();
  const sortedSections = [...sections].sort(sortByFractionalOrder);
  const pendingSectionIds = new Set<string>(
    sortedSections.map((section) => section._id || section.uniqueKeyId)
  );
  const sectionKeys = new Set<string>(
    sections.flatMap((section) => {
      const keys = [section.uniqueKeyId];
      if (section._id) {
        keys.push(section._id);
      }
      return keys;
    })
  );

  const isParentResolved = (parentSectionId: string | null): boolean => {
    if (!parentSectionId) {
      return true;
    }

    if (!sectionKeys.has(parentSectionId)) {
      return true;
    }

    return createdSectionIdentifierByKey.has(parentSectionId);
  };

  while (pendingSectionIds.size > 0) {
    let createdInIteration = 0;

    for (const section of sortedSections) {
      const sectionKey = section._id || section.uniqueKeyId;
      if (!pendingSectionIds.has(sectionKey)) {
        continue;
      }
      if (!isParentResolved(section.parentSectionId)) {
        continue;
      }

      const parentSectionId = section.parentSectionId
        ? createdSectionIdentifierByKey.get(section.parentSectionId) ||
          section.parentSectionId
        : undefined;

      const createdSection = await createChecklistSection({
        title: section.title,
        description: section.description,
        parentSectionId,
        projectId,
        inspectionTemplateId: templateId,
      });

      if (!createdSection?._id) {
        throw new Error('Checklist section creation returned empty section id');
      }

      createdSectionIdentifierByKey.set(section.uniqueKeyId, createdSection._id);
      if (section._id) {
        createdSectionIdentifierByKey.set(section._id, createdSection._id);
      }
      pendingSectionIds.delete(sectionKey);
      createdInIteration += 1;
    }

    if (createdInIteration === 0) {
      throw new Error(
        'Cannot resolve checklist section hierarchy while creating template'
      );
    }
  }

  return createdSectionIdentifierByKey;
}

type CreateChecklistItemsFn<TItem> = (
  items: TItem[]
) => Promise<ChecklistItemInDto[]>;

interface NodeToCreate {
  type: 'section' | 'item';
  order: string;
  parentSectionId: string | null;
  data: TemplateSectionDraft | TemplateChecklistItemDraft;
}

export interface CreateNodesResult {
  sectionIdMap: Map<string, string>;
  createdItemsByUniqueKeyId: Map<string, ChecklistItemInDto>;
}

export async function createNodesInOrder<
  TSection extends TemplateSectionDraft,
  TItem extends TemplateChecklistItemDraft,
>(
  templateId: string,
  projectId: string,
  sections: TSection[],
  items: TItem[],
  createSection: CreateChecklistSectionFn,
  createItems: CreateChecklistItemsFn<TItem>
): Promise<CreateNodesResult> {
  const sectionIdMap = new Map<string, string>();
  const createdItemsByUniqueKeyId = new Map<string, ChecklistItemInDto>();

  const sectionsByParent = new Map<string | null, TSection[]>();
  const itemsByParent = new Map<string | null, TItem[]>();

  sections.forEach((section) => {
    const parentId = section.parentSectionId;
    if (!sectionsByParent.has(parentId)) {
      sectionsByParent.set(parentId, []);
    }
    sectionsByParent.get(parentId)!.push(section);
  });

  items.forEach((item) => {
    const parentId = item.parentSectionId;
    if (!itemsByParent.has(parentId)) {
      itemsByParent.set(parentId, []);
    }
    itemsByParent.get(parentId)!.push(item);
  });

  async function processLevel(parentId: string | null): Promise<void> {
    const mappedParentId = parentId ? sectionIdMap.get(parentId) || parentId : null;

    const levelSections = sectionsByParent.get(parentId) || [];
    const levelItems = itemsByParent.get(parentId) || [];

    const allNodes: NodeToCreate[] = [
      ...levelSections.map((s) => ({
        type: 'section' as const,
        order: s.order,
        parentSectionId: mappedParentId,
        data: s,
      })),
      ...levelItems.map((i) => ({
        type: 'item' as const,
        order: i.order,
        parentSectionId: mappedParentId,
        data: i,
      })),
    ];

    allNodes.sort(sortByFractionalOrder);

    let currentItemBatch: TItem[] = [];

    async function flushItemBatch(): Promise<void> {
      if (currentItemBatch.length === 0) return;

      const itemsToCreate = currentItemBatch.map((item) => ({
        ...item,
        parentSectionId: mappedParentId,
      })) as TItem[];

      const createdItems = await createItems(itemsToCreate);

      currentItemBatch.forEach((item, index) => {
        const createdItem = createdItems[index];
        if (createdItem?._id) {
          createdItemsByUniqueKeyId.set(item.uniqueKeyId, createdItem);
        }
      });

      currentItemBatch = [];
    }

    for (const node of allNodes) {
      if (node.type === 'section') {
        await flushItemBatch();

        const section = node.data as TSection;
        const createdSection = await createSection({
          title: section.title,
          description: section.description,
          parentSectionId: mappedParentId || undefined,
          projectId,
          inspectionTemplateId: templateId,
        });

        if (!createdSection?._id) {
          throw new Error('Section creation returned empty id');
        }

        sectionIdMap.set(section.uniqueKeyId, createdSection._id);
        if (section._id) {
          sectionIdMap.set(section._id, createdSection._id);
        }

        await processLevel(section._id || section.uniqueKeyId);
      } else {
        currentItemBatch.push(node.data as TItem);
      }
    }

    await flushItemBatch();
  }

  await processLevel(null);

  return { sectionIdMap, createdItemsByUniqueKeyId };
}

export function mapChecklistSectionReferences<
  TChecklistItem extends Pick<TemplateChecklistItemDraft, 'parentSectionId'>,
>(
  checklist: TChecklistItem[],
  createdSectionIdentifierByKey: Map<string, string>
): TChecklistItem[] {
  return checklist.map((item) => {
    if (!item.parentSectionId) {
      return item;
    }

    const mappedSectionId = createdSectionIdentifierByKey.get(item.parentSectionId);

    if (!mappedSectionId) {
      return item;
    }

    return {
      ...item,
      parentSectionId: mappedSectionId,
    };
  });
}